import type { MisspelledWord } from '../types/misspelled-word'
import type { SpellChecker } from '../types/spell-checker'
import type { SpellLayers } from '../types/spell-layers'
import { accentedSpellingOf } from './accented-spelling-of'
import { buildLayerLookup } from './build-layer-lookup'
import { stripAccents } from './strip-accents'
import { tokenizeWords } from './tokenize-words'

/** Paragraphs remembered per scanner. Past this the memory is dropped and refilled as text is read. */
const MAX_CACHED_PARAGRAPHS = 2000

export interface SpellScanner {
  /** Every misspelled word in `text`, with offsets into `text`. Paragraphs are split on `\n`. */
  scan: (text: string) => MisspelledWord[]
}

/**
 * A scanner for one wordlist and one set of dictionary layers. It remembers the result for each
 * paragraph's text, so rescanning after an edit only checks the paragraphs that changed, which is
 * what keeps always-on checking cheap while the user types. The memory belongs to the scanner:
 * build a new one when the wordlist, the layers or the case rule change.
 *
 * Only the position-independent part is remembered. Where the caret is and which text is hidden
 * change without the paragraph changing, so callers apply those after scanning.
 */
export function createSpellScanner(checker: SpellChecker, layers: SpellLayers): SpellScanner {
  const inLayers = buildLayerLookup(layers)
  const byParagraph = new Map<string, MisspelledWord[]>()

  // The en_US wordlist has "cafe" but not "café". Accents are part of the word, so an accented
  // word is right when its unaccented base is a word, and a word that needs its accent is not.
  const isKnown = (word: string) => {
    if (accentedSpellingOf(word)) return false
    if (checker.isCorrect(word)) return true

    const base = stripAccents(word)

    return base !== word && checker.isCorrect(base)
  }

  const scanParagraph = (paragraph: string): MisspelledWord[] => {
    const remembered = byParagraph.get(paragraph)

    if (remembered) return remembered

    const found = tokenizeWords(paragraph)
      .filter(
        ({ word, compound }) =>
          !isKnown(word) && !inLayers(word) && !(compound && inLayers(compound))
      )
      .map(({ word, start }) => ({ word, start, end: start + word.length }))

    if (byParagraph.size >= MAX_CACHED_PARAGRAPHS) byParagraph.clear()
    byParagraph.set(paragraph, found)

    return found
  }

  return {
    scan: (text) => {
      const words: MisspelledWord[] = []
      let paragraphStart = 0

      for (const paragraph of text.split('\n')) {
        for (const { word, start, end } of scanParagraph(paragraph)) {
          words.push({ word, start: paragraphStart + start, end: paragraphStart + end })
        }

        paragraphStart += paragraph.length + 1
      }

      return words
    },
  }
}
